import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Save, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { productService } from '../../services/productService';
import { Location } from '../../types';

function LocationForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    magasin: 'Dépôt Principal',
    allee: '',
    travee: '',
    bac: '',
    commentaire: ''
  });

  const isEditing = !!id;

  useEffect(() => {
    if (id) {
      loadLocation();
    }
  }, [id]);

  const loadLocation = async () => {
    if (!id) return;
    
    try {
      const locations = await productService.getLocations();
      const locationData = locations.find(l => l.id === id);
      
      if (locationData) {
        setLocation(locationData);
        setFormData({
          magasin: locationData.magasin,
          allee: locationData.allee,
          travee: locationData.travee,
          bac: locationData.bac,
          commentaire: locationData.commentaire || ''
        });
      }
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger l\'emplacement',
        type: 'error'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      
      if (isEditing && location) {
        await productService.updateLocation(location.id, {
          ...formData,
          isActive: location.isActive
        });
        
        addNotification({
          userId: user.id,
          title: 'Emplacement modifié',
          message: 'L\'emplacement a été modifié avec succès',
          type: 'success'
        });
      } else {
        await productService.createLocation({
          ...formData,
          isActive: true
        });
        
        addNotification({
          userId: user.id,
          title: 'Emplacement créé',
          message: 'L\'emplacement a été créé avec succès',
          type: 'success'
        });
      }
      
      navigate('/products/locations');
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: error instanceof Error ? error.message : 'Impossible de sauvegarder l\'emplacement',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const previewCode = `${formData.magasin.substring(0, 2).toUpperCase()}-${formData.allee}${formData.travee}-${formData.bac}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/products/locations')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg">
              <MapPin className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Modifier l\'emplacement' : 'Nouvel emplacement'}
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-8 sm:px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Magasin/Dépôt *
                </label>
                <input
                  type="text"
                  value={formData.magasin}
                  onChange={(e) => setFormData({ ...formData, magasin: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="Dépôt Principal"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allée *
                </label>
                <input
                  type="text"
                  value={formData.allee}
                  onChange={(e) => setFormData({ ...formData, allee: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="A"
                  maxLength={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Travée *
                </label>
                <input
                  type="text"
                  value={formData.travee}
                  onChange={(e) => setFormData({ ...formData, travee: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="1"
                  maxLength={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bac *
                </label>
                <input
                  type="text"
                  value={formData.bac}
                  onChange={(e) => setFormData({ ...formData, bac: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="B01"
                  maxLength={3}
                />
              </div>
            </div>

            {/* Aperçu du code */}
            {(formData.magasin && formData.allee && formData.travee && formData.bac) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">
                    Code généré: <span className="font-mono text-lg">{previewCode}</span>
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commentaire
              </label>
              <input
                type="text"
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Description de l'emplacement"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/products/locations')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LocationForm;